import { getPartnerToken, getPartnerRefresh, getPartnerRestaurantId } from '@/services/auth/partnerSessionStorage';
import axios from 'axios';
import { getDeviceKey } from '@/services/device/deviceCredential';
import { getPendingActionStats } from '@/services/offline/pendingActionQueue';
import { isPendingActionSyncRunning } from '@/services/offline/pendingActionSyncEngine';
import { getActiveRestaurantContext } from '@/services/offline/restaurantOfflineData';
import { LOCAL_DB_VERSION } from '@/services/localDb/schema';
import { runQrReceiverTick } from '@/services/device/qrOrderReceiverService';
import {
  CONNECTIVITY_MODES,
  connectivityStatus,
  subscribeToConnectivityChanges,
} from '@/services/connectivity/connectivityService';

const LEADER_LEASE_KEY = 'ewu_tpv_device_heartbeat_leader';
const LEADER_LEASE_MS = 15000;
const COORDINATOR_TICK_MS = 5000;
const DEFAULT_HEARTBEAT_INTERVAL_MS = 10000;
const BLOCKED_STATUS_INTERVAL_MS = 8000;
const POLICY_DISCOVERY_INTERVAL_MS = 30000;
const CHANNEL_NAME = 'ewu-device-heartbeat';

const tabId = typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

let activeStore = null;
let coordinatorTimer = null;
let broadcastChannel = null;
let connectivityUnsubscribe = null;
let heartbeatIntervalMs = DEFAULT_HEARTBEAT_INTERVAL_MS;
let lastAttemptAt = 0;
let lastAccessCheckAt = 0;
let nextAllowedAttemptAt = 0;
let consecutiveFailures = 0;
let listenersInstalled = false;
let heartbeatRequest = null;

function readToken() {
  const token = activeStore?.state?.partner_token || getPartnerToken();
  return token && token !== 'null' && token !== 'undefined' ? token : null;
}

function coordinationScope() {
  return `${getPartnerRestaurantId() || 'legacy'}:${activeStore?.state?.device?.deviceId || 'browser'}`;
}
const leaseKey = () => `${LEADER_LEASE_KEY}:${coordinationScope()}`;

function readLease() {
  try {
    const value = JSON.parse(localStorage.getItem(leaseKey()) || 'null');
    return value && value.tabId && Number(value.expiresAt) ? value : null;
  } catch {
    return null;
  }
}

function writeLease(expiresAt) {
  try {
    localStorage.setItem(leaseKey(), JSON.stringify({ tabId, expiresAt }));
    return true;
  } catch {
    return true;
  }
}

function tryBecomeLeader() {
  const now = Date.now();
  const lease = readLease();
  const canLead = !lease || lease.tabId === tabId || lease.expiresAt <= now;
  if (canLead) writeLease(now + LEADER_LEASE_MS);
  const leader = canLead;
  activeStore?.commit('device/setHeartbeatLeader', leader);
  return leader;
}

function releaseLeadership() {
  try {
    const lease = readLease();
    if (lease?.tabId === tabId) localStorage.removeItem(leaseKey());
  } catch { /* Lease expires naturally. */ }
  activeStore?.commit('device/setHeartbeatLeader', false);
}

function broadcast(message) {
  try { broadcastChannel?.postMessage({ ...message, sender: tabId }); } catch { /* Optional channel. */ }
}

function installChannel() {
  if (broadcastChannel || typeof BroadcastChannel === 'undefined') return;
  broadcastChannel = new BroadcastChannel(`${CHANNEL_NAME}:${coordinationScope()}`);
  broadcastChannel.onmessage = (event) => {
    if (event.data?.sender === tabId) return;
    if (event.data?.type === 'heartbeat-result') {
      activeStore?.commit('device/setHeartbeatSuccess', event.data.payload);
    } else if (event.data?.type === 'operational-status-result') {
      activeStore?.commit('device/setOperationalStatus', event.data.payload);
    }
  };
}

export async function getLocalReportedDeviceState() {
  if (!activeStore) return { reportedState: 'UNKNOWN', pendingActionsCount: 0, conflictCount: 0 };
  if ([CONNECTIVITY_MODES.BROWSER_OFFLINE, CONNECTIVITY_MODES.BACKEND_UNAVAILABLE].includes(connectivityStatus.mode)) {
    return {
      reportedState: activeStore.state.device.device?.is_primary ? 'OFFLINE_ACTIVE' : 'API_UNAVAILABLE',
      pendingActionsCount: 0,
      conflictCount: 0,
    };
  }

  const context = await getActiveRestaurantContext({ preferCached: true });
  const stats = context.restaurant_id
    ? await getPendingActionStats(context.restaurant_id)
    : { pending: 0, processing: 0, failed: 0, conflicts: 0 };
  const pendingActionsCount = Number(stats.pending || 0) + Number(stats.processing || 0) + Number(stats.failed || 0);
  const conflictCount = Number(stats.conflicts || 0);
  let reportedState = 'ONLINE_SYNCED';
  if (conflictCount > 0) reportedState = 'CONFLICT';
  else if (isPendingActionSyncRunning() || Number(stats.processing || 0) > 0) reportedState = 'SYNCING';
  else if (pendingActionsCount > 0) reportedState = 'ONLINE_PENDING';
  return { reportedState, pendingActionsCount, conflictCount };
}

export function setLocalReportedDeviceState(state) {
  activeStore?.commit('device/setLocalReportedState', state);
}

async function refreshAccessToken() {
  const refresh = getPartnerRefresh();
  if (!refresh || refresh === 'null') return false;
  try {
    const response = await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/partner/refresh`, { refresh });
    if (getPartnerRefresh() !== refresh) return false;
    activeStore?.commit('login', response.data);
    return Boolean(response.data?.access);
  } catch {
    return false;
  }
}

function shouldStopForDeviceError(error) {
  const code = error?.response?.data?.code;
  return ['DEVICE_REVOKED', 'DEVICE_KEY_INVALID', 'DEVICE_KEY_MISSING', 'DEVICE_NOT_REGISTERED'].includes(code);
}

export function sendDeviceHeartbeatNow(options = {}) {
  if (heartbeatRequest) return heartbeatRequest;
  heartbeatRequest = performDeviceHeartbeat(options).finally(() => {
    heartbeatRequest = null;
  });
  return heartbeatRequest;
}

async function performDeviceHeartbeat(options = {}) {
  const { retryAuth = true, forceLeadership = false } = options;
  if (!activeStore || !readToken()) return null;
  const state = activeStore.state.device;
  if (!state.presenceEnabled || !state.registered || !state.device?.is_enabled) return null;
  if (!(forceLeadership ? tryBecomeLeader() : state.heartbeatLeader || tryBecomeLeader())) return null;

  const deviceKey = await getDeviceKey(state.deviceId);
  if (!deviceKey) {
    activeStore.commit('device/setHeartbeatError', { message: 'Este dispositivo necesita una credencial emitida por un Partner.', code: 'DEVICE_KEY_MISSING' });
    return null;
  }

  const local = await getLocalReportedDeviceState();
  setLocalReportedDeviceState(local.reportedState);
  if (['OFFLINE_ACTIVE', 'API_UNAVAILABLE'].includes(local.reportedState)) return null;

  lastAttemptAt = Date.now();
  activeStore.commit('device/setHeartbeatSentAt', new Date().toISOString());
  try {
    const response = await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/heartbeat`, {
      reported_state: local.reportedState,
      pending_actions_count: local.pendingActionsCount,
      conflict_count: local.conflictCount,
      local_cursor: null,
      server_cursor: null,
      app_version: process.env.VUE_APP_VERSION || '0.1.0',
      offline_schema_version: LOCAL_DB_VERSION,
    }, {
      headers: {
        Authorization: `Bearer ${readToken()}`,
        'X-EWU-Device-Key': deviceKey,
      },
      timeout: 8000,
    });
    consecutiveFailures = 0;
    nextAllowedAttemptAt = 0;
    heartbeatIntervalMs = Math.max(5000, Number(response.data?.heartbeat_interval_seconds || 10) * 1000);
    const heartbeatPayload = {
      ...response.data,
      __ewu_operational_request_id: response.config?.__ewuOperationalRequestId,
    };
    activeStore.commit('device/setHeartbeatSuccess', heartbeatPayload);
    broadcast({ type: 'heartbeat-result', payload: heartbeatPayload });
    return response.data;
  } catch (error) {
    if (Number(error?.response?.status) === 401 && retryAuth && await refreshAccessToken()) {
      return performDeviceHeartbeat({ retryAuth: false, forceLeadership: true });
    }
    consecutiveFailures += 1;
    nextAllowedAttemptAt = Date.now() + Math.min(60000, (2 ** Math.min(consecutiveFailures, 5)) * 2000);
    activeStore.commit('device/setHeartbeatError', {
      message: error?.response?.data?.error || error?.message || 'No se pudo enviar el heartbeat.',
      code: error?.response?.data?.code || (error?.response ? 'HEARTBEAT_ERROR' : 'BACKEND_UNAVAILABLE'),
    });
    if (shouldStopForDeviceError(error)) stopDeviceHeartbeat();
    return null;
  }
}

async function coordinatorTick() {
  if (!activeStore || !readToken()) {
    stopDeviceHeartbeat();
    return;
  }
  if (document.visibilityState === 'hidden') {
    releaseLeadership();
    return;
  }
  const leader = tryBecomeLeader();
  if (!leader) return;
  const now = Date.now();
  const state = activeStore.state.device;
  const deviceKey = state.deviceId ? await getDeviceKey(state.deviceId) : null;
  const heartbeatEligible = state.presenceEnabled && state.registered && state.device?.is_enabled && deviceKey;
  const accessCheckIntervalMs = state.secondaryBlockingEnabled
    ? BLOCKED_STATUS_INTERVAL_MS
    : POLICY_DISCOVERY_INTERVAL_MS;
  if (!heartbeatEligible
    && connectivityStatus.mode === CONNECTIVITY_MODES.ONLINE
    && now - lastAccessCheckAt >= accessCheckIntervalMs) {
    lastAccessCheckAt = now;
    try {
      const payload = await activeStore.dispatch('device/refreshOperationalAccess');
      broadcast({ type: 'operational-status-result', payload });
    } catch { /* Store preserves the blocking reason. */ }
  }
  if (heartbeatEligible && now >= nextAllowedAttemptAt && now - lastAttemptAt >= heartbeatIntervalMs) {
    await sendDeviceHeartbeatNow();
  }
  if (
    heartbeatEligible
    && connectivityStatus.mode === CONNECTIVITY_MODES.ONLINE
  ) {
    await runQrReceiverTick({
      token: readToken(),
      deviceId: state.deviceId,
      deviceKey,
      operationMode: state.restaurantOperationMode || 'CLOUD_MODE',
    });
  }
}

function onResume() {
  if (document.visibilityState === 'visible' && tryBecomeLeader()) {
    lastAttemptAt = 0;
    void sendDeviceHeartbeatNow({ forceLeadership: true });
  }
}

function onIdentityReset() {
  stopDeviceHeartbeat();
}

function installListeners() {
  if (listenersInstalled) return;
  listenersInstalled = true;
  window.addEventListener('focus', onResume);
  window.addEventListener('online', onResume);
  window.addEventListener('ewu-device-identity-reset', onIdentityReset);
  document.addEventListener('visibilitychange', onResume);
  connectivityUnsubscribe = subscribeToConnectivityChanges((status) => {
    if (status.mode === CONNECTIVITY_MODES.ONLINE && tryBecomeLeader()) {
      lastAttemptAt = 0;
      void sendDeviceHeartbeatNow({ forceLeadership: true });
    } else if ([CONNECTIVITY_MODES.BROWSER_OFFLINE, CONNECTIVITY_MODES.BACKEND_UNAVAILABLE].includes(status.mode)) {
      activeStore?.commit('device/setSecondaryApiUnavailable', {
        message: status.backendFailureReason || 'No se puede conectar con la API de EWU.',
      });
    }
  });
}

function removeListeners() {
  if (!listenersInstalled) return;
  listenersInstalled = false;
  window.removeEventListener('focus', onResume);
  window.removeEventListener('online', onResume);
  window.removeEventListener('ewu-device-identity-reset', onIdentityReset);
  document.removeEventListener('visibilitychange', onResume);
  connectivityUnsubscribe?.();
  connectivityUnsubscribe = null;
}

export async function startDeviceHeartbeat(store) {
  activeStore = store || activeStore;
  const state = activeStore?.state?.device;
  if (!state || !readToken()) return false;
  // Keep the single-tab coordinator alive even before rollout activation. This
  // lets an already-open client discover a newly enabled policy without reload,
  // including browsers that lost their local device credential.
  if (coordinatorTimer) return true;

  installChannel();
  installListeners();
  activeStore.commit('device/setHeartbeatStarted', true);
  tryBecomeLeader();
  coordinatorTimer = window.setInterval(() => { void coordinatorTick(); }, COORDINATOR_TICK_MS);
  lastAttemptAt = 0;
  lastAccessCheckAt = 0;
  await coordinatorTick();
  return true;
}

export function stopDeviceHeartbeat() {
  if (coordinatorTimer) window.clearInterval(coordinatorTimer);
  coordinatorTimer = null;
  releaseLeadership();
  removeListeners();
  try { broadcastChannel?.close(); } catch { /* Optional channel. */ }
  broadcastChannel = null;
  activeStore?.commit('device/setHeartbeatStarted', false);
}
